import React, { useState, useEffect, useContext } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { AuthContext } from "../../../contexts/AuthContext";
import { proporcionDe } from "../../../desgloseOC";
import { ordenTallas, rangoTalla } from "../../../desglosePedido";

// Divide un prepedido en varios pedidos: una columna por pedido nuevo (con su
// fecha, OC del cliente y observaciones) y un renglon por MODELO del prepedido
// (articulo + renglon).
//
// - En el renglon del modelo se captura el PORCENTAJE que se lleva cada pedido
//   (como la proporcion de PedidosCreate); esas piezas se reparten solas entre
//   sus colores y tallas con la proporcion del prepedido (restos mayores).
// - El modelo se abre para ver ese reparto por color y talla y corregirlo a
//   mano: al editar una celda el modelo pasa a "manual" y su porcentaje se
//   calcula de las piezas. Volver a escribir un porcentaje regresa al reparto
//   automatico.
//
// Al back le llega por linea como siempre. Ninguna linea puede repartir mas de
// lo que tiene; lo que no se reparte se pierde (el prepedido queda "Dividido").
const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
const URL_ARTICULOS_PEDIDO = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

// Reparte "total" piezas entre lineas en proporcion a lo que les queda (resto),
// por restos mayores; nunca le da a una linea mas de lo que le queda.
function repartePorLinea(lineas, resto, total) {
  const disp = lineas.reduce((s, l) => s + resto[l._id], 0);
  if (total <= 0 || disp <= 0) return {};
  const t = Math.min(total, disp);
  const filas = lineas.map((l) => {
    const exacto = (resto[l._id] * t) / disp;
    return { id: l._id, q: Math.floor(exacto), frac: exacto - Math.floor(exacto), max: resto[l._id] };
  });
  let falta = t - filas.reduce((s, f) => s + f.q, 0);
  [...filas].sort((a, b) => b.frac - a.frac).forEach((f) => {
    if (falta > 0 && f.q < f.max) { f.q += 1; falta -= 1; }
  });
  return Object.fromEntries(filas.filter((f) => f.q > 0).map((f) => [f.id, f.q]));
}
const nuevoPedido = (i) => ({ key: `p${i}_${Date.now()}`, fecha: moment().format("YYYY-MM-DD"), observaciones: "", ordenCompraCliente: "", cant: {} });

function DividirPrepedidoModal({ abierto, cerrar, prepedido, onDividido }) {
  const { user } = useContext(AuthContext);
  // un renglon por modelo (articulo + renglon) con sus lineas por color y talla
  const [lineas, setLineas] = useState([]);
  const [pedidos, setPedidos] = useState([nuevoPedido(1), nuevoPedido(2)]);
  // modelos corregidos a mano: { [modelo]: { [pedido]: { [linea]: piezas } } }
  const [manual, setManual] = useState({});
  const [abiertos, setAbiertos] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto || !prepedido) return;
    setPedidos([nuevoPedido(1), nuevoPedido(2)]);
    setManual({});
    setAbiertos({});
    setLineas([]);
    axios.get(`${URL_ARTICULOS_PEDIDO}/pedidos/${prepedido._id}`, { headers: headers() })
      .then((r) => {
        const porModelo = new Map();
        (r.data || []).filter((a) => a.articulos?.[0] && a.is_active !== "No").forEach((a) => {
          const art = a.articulos[0];
          const renglon = a.renglon || 1;
          const k = `${art._id}|${renglon}`;
          if (!porModelo.has(k)) porModelo.set(k, { _id: k, renglon, modelo: `${art.codigo} ${art.nombre}`, cantidad: 0, lineas: [] });
          const m = porModelo.get(k);
          const cantidad = Number(a.cantidad) || 0;
          m.cantidad += cantidad;
          m.lineas.push({ _id: a._id, color: a.colores?.[0]?.name || "NA", talla: a.tallas?.[0]?.name || "NA", cantidad });
        });
        const modelos = [...porModelo.values()].sort((a, b) => a.renglon - b.renglon);
        modelos.forEach((m) => {
          m.lineas.sort((a, b) => (a.color > b.color ? 1 : a.color < b.color ? -1 : rangoTalla(a.talla) - rangoTalla(b.talla)));
          const porColor = {}, porTalla = {};
          m.lineas.forEach((l) => {
            porColor[l.color] = (porColor[l.color] || 0) + l.cantidad;
            porTalla[l.talla] = (porTalla[l.talla] || 0) + l.cantidad;
          });
          const cols = Object.keys(porColor), tals = ordenTallas(Object.keys(porTalla));
          m.colores = cols.join("-") + (cols.length > 1 ? ` (${proporcionDe(cols.map((c) => porColor[c]))})` : "");
          m.tallas = tals.join("-") + (tals.length > 1 ? ` (${proporcionDe(tals.map((t) => porTalla[t]))})` : "");
        });
        setLineas(modelos);
      })
      .catch((e) => console.log(e));
  }, [abierto, prepedido]); // eslint-disable-line

  // --- reparto automatico (por porcentaje) ---
  const pct = (p, idM) => Math.max(0, Number(p.cant[idM]) || 0);
  // De porcentajes a piezas por pedido, por restos mayores
  const piezasPorPct = (m) => {
    const exactos = pedidos.map((p) => (m.cantidad * pct(p, m._id)) / 100);
    const meta = Math.min(m.cantidad, Math.round(exactos.reduce((s, x) => s + x, 0)));
    const q = exactos.map((x) => Math.floor(x));
    let falta = meta - q.reduce((s, x) => s + x, 0);
    exactos.map((x, i) => ({ i, f: x - Math.floor(x) })).sort((a, b) => b.f - a.f).forEach(({ i }) => {
      if (falta > 0 && pct(pedidos[i], m._id) > 0) { q[i] += 1; falta -= 1; }
    });
    return q;
  };
  // Piezas de cada linea para cada pedido: cada pedido toma su parte de lo que
  // le queda a cada color y talla, en la proporcion del prepedido
  const repartoAuto = (m) => {
    const q = piezasPorPct(m);
    const resto = Object.fromEntries(m.lineas.map((l) => [l._id, l.cantidad]));
    const out = {};
    pedidos.forEach((p, i) => {
      const r = repartePorLinea(m.lineas, resto, q[i]);
      Object.entries(r).forEach(([id, n]) => { resto[id] -= n; });
      out[p.key] = r;
    });
    return out;
  };
  // Lo que vale: lo corregido a mano o, si no, el automatico
  const valores = Object.fromEntries(lineas.map((m) => [m._id, manual[m._id] || repartoAuto(m)]));
  const piezasModeloPedido = (m, p) => Object.values(valores[m._id]?.[p.key] || {}).reduce((s, n) => s + (Number(n) || 0), 0);
  const repartidoLinea = (m, l) => pedidos.reduce((s, p) => s + (Number(valores[m._id]?.[p.key]?.[l._id]) || 0), 0);
  const repartidoModelo = (m) => pedidos.reduce((s, p) => s + piezasModeloPedido(m, p), 0);
  // el porcentaje que se ve: el capturado o, si es manual, el que dan las piezas
  const pctVisible = (m, p) => (manual[m._id]
    ? (m.cantidad ? Number(((piezasModeloPedido(m, p) * 100) / m.cantidad).toFixed(1)) : 0)
    : (p.cant[m._id] ?? ""));

  // escribir un porcentaje: el modelo vuelve al reparto automatico (los demas
  // pedidos se quedan con el porcentaje que daban sus piezas)
  const cambiaPct = (key, idM, valor) => {
    const m = lineas.find((x) => x._id === idM);
    const fijos = Object.fromEntries(pedidos.map((p) => [p.key, p.key === key ? valor
      : (manual[idM] ? (m.cantidad ? Number(((piezasModeloPedido(m, p) * 100) / m.cantidad).toFixed(2)) : 0) : p.cant[idM])]));
    setPedidos((prev) => prev.map((p) => ({ ...p, cant: { ...p.cant, [idM]: fijos[p.key] } })));
    if (manual[idM]) setManual((prev) => { const n = { ...prev }; delete n[idM]; return n; });
  };
  // corregir una celda de color y talla: el modelo pasa a manual con lo que tenia
  const cambiaVariante = (m, key, idL, valor) => {
    setManual((prev) => {
      const base = prev[m._id] || JSON.parse(JSON.stringify(repartoAuto(m)));
      const delPedido = { ...(base[key] || {}), [idL]: valor === "" ? "" : Math.max(0, Number(valor) || 0) };
      return { ...prev, [m._id]: { ...base, [key]: delPedido } };
    });
  };
  const cambiaCampo = (key, campo, valor) =>
    setPedidos((prev) => prev.map((p) => (p.key === key ? { ...p, [campo]: valor } : p)));
  const quitaPedido = (key) => {
    setPedidos((prev) => (prev.length > 1 ? prev.filter((p) => p.key !== key) : prev));
    setManual((prev) => Object.fromEntries(Object.entries(prev).map(([idM, v]) => { const n = { ...v }; delete n[key]; return [idM, n]; })));
  };
  // "Todo lo que falta" a esta columna: el porcentaje que le queda a cada modelo
  // (o, si el modelo esta a mano, lo que le queda a cada color y talla)
  const llenaResto = (key) => {
    const manuales = {};
    lineas.forEach((m) => {
      if (!manual[m._id]) return;
      const delPedido = { ...(manual[m._id][key] || {}) };
      m.lineas.forEach((l) => {
        const otros = pedidos.filter((p) => p.key !== key).reduce((s, p) => s + (Number(manual[m._id][p.key]?.[l._id]) || 0), 0);
        delPedido[l._id] = Math.max(0, l.cantidad - otros);
      });
      manuales[m._id] = { ...manual[m._id], [key]: delPedido };
    });
    if (Object.keys(manuales).length) setManual((prev) => ({ ...prev, ...manuales }));
    setPedidos((prev) => prev.map((p) => {
      if (p.key !== key) return p;
      const cant = { ...p.cant };
      lineas.forEach((l) => {
        if (manual[l._id]) return;
        const otros = prev.filter((x) => x.key !== key).reduce((s, x) => s + (Number(x.cant[l._id]) || 0), 0);
        cant[l._id] = Math.max(0, Number((100 - otros).toFixed(2)));
      });
      return { ...p, cant };
    }));
  };

  const piezasPedido = (p) => lineas.reduce((s, m) => s + piezasModeloPedido(m, p), 0);
  const total = lineas.reduce((s, m) => s + m.cantidad, 0);
  const sinRepartir = lineas.reduce((s, m) => s + Math.max(0, m.cantidad - repartidoModelo(m)), 0);
  // problemas: porcentaje de mas (automatico) o una linea repartida de mas (manual)
  const pctDeMas = lineas.filter((m) => !manual[m._id] && pedidos.reduce((s, p) => s + pct(p, m._id), 0) > 100.0001);
  const lineasDeMas = [];
  lineas.forEach((m) => m.lineas.forEach((l) => { if (repartidoLinea(m, l) > l.cantidad) lineasDeMas.push(`${m.modelo} ${l.color} ${l.talla}`); }));

  async function dividir() {
    if (pctDeMas.length) {
      Swal.fire({ icon: "error", title: "Más del 100%", text: `${pctDeMas.map((m) => m.modelo).join(", ")}: se reparte más del 100% del modelo.` });
      return;
    }
    if (lineasDeMas.length) {
      Swal.fire({ icon: "error", title: "Piezas de más", text: `${lineasDeMas.slice(0, 5).join(", ")}${lineasDeMas.length > 5 ? "..." : ""}: se reparten más piezas de las que tiene el prepedido.` });
      return;
    }
    const conPiezas = pedidos.filter((p) => piezasPedido(p) > 0);
    if (!conPiezas.length) {
      Swal.fire({ icon: "warning", title: "Sin cantidades", text: "Asigna un porcentaje (o piezas) a por lo menos un pedido." });
      return;
    }
    const ok = await Swal.fire({
      icon: "question",
      title: `Se crearán ${conPiezas.length} pedido(s)`,
      html: conPiezas.map((p, i) => `Pedido ${i + 1}: ${num(piezasPedido(p))} pzas · ${p.fecha}${p.ordenCompraCliente ? ` · OC ${p.ordenCompraCliente}` : ""}`).join("<br>") +
        (sinRepartir > 0 ? `<br><br><b style="color:#b45309">${num(sinRepartir)} pzas sin repartir se quedan fuera.</b>` : "") +
        `<br><br>El prepedido ${prepedido.numero} quedará como <b>Dividido</b>.`,
      showCancelButton: true, confirmButtonText: "Sí, dividir", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const cuerpo = conPiezas.map((p) => {
        const porLinea = [];
        lineas.forEach((m) => Object.entries(valores[m._id]?.[p.key] || {}).forEach(([id, q]) => {
          if (Number(q) > 0) porLinea.push({ articulosPedido: id, cantidad: Number(q) });
        }));
        return { fecha: p.fecha, observaciones: p.observaciones, ordenCompraCliente: p.ordenCompraCliente, lineas: porLinea };
      });
      const r = await axios.post(`${URL_PEDIDOS}/dividir/${prepedido._id}`, { user: user.id, pedidos: cuerpo }, { headers: headers() });
      setGuardando(false);
      const folios = (r.data.pedidos || []).map((x) => x.idPedido).join(", ");
      await Swal.fire({ icon: "success", title: "Prepedido dividido", text: `Pedidos creados: ${folios}` });
      cerrar();
      if (onDividido) onDividido();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo dividir", text: `${error.response?.data?.message || error.response?.data || error.message}` });
    }
  }

  const colorResto = (resto, deMas) => (deMas ? "#dc2626" : resto > 0 ? "#b45309" : "#15803d");

  return (
    <Modal size="xxl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        Dividir prepedido {prepedido?.numero} · {prepedido?.clientes}
      </ModalHeader>
      <ModalBody>
        <p style={{ color: "#64748b", margin: 0 }}>
          Una columna por pedido nuevo y un renglón por modelo. Captura qué <b>porcentaje</b> de cada modelo se lleva cada pedido
          (debajo se ve en piezas); se reparte entre sus colores y tallas con la misma proporción del prepedido.
          Con la flecha abres el modelo por <b>color y talla</b> y puedes corregir las piezas a mano.
          Los pedidos son del mismo cliente del prepedido y heredan vendedor, almacén e impuestos.
        </p>
        <br />
        <div style={{ overflowX: "auto" }}>
          <Table size="sm" bordered style={{ whiteSpace: "nowrap" }}>
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>#</th><th>Modelo</th><th>Colores</th><th>Tallas</th>
                <th className="text-right">Prepedido</th>
                {pedidos.map((p, i) => (
                  <th key={p.key} style={{ minWidth: 170, background: "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Pedido {i + 1}</span>
                      {pedidos.length > 1 ? (
                        <Button close onClick={() => quitaPedido(p.key)} title="Quitar esta columna" />
                      ) : null}
                    </div>
                    <Input bsSize="sm" type="date" value={p.fecha}
                      onChange={(e) => { const v = e.target.value; cambiaCampo(p.key, "fecha", v); }} />
                    <Input bsSize="sm" type="text" placeholder="OC del cliente" value={p.ordenCompraCliente}
                      title="Numero de orden de compra que da el cliente para este pedido"
                      onChange={(e) => { const v = e.target.value; cambiaCampo(p.key, "ordenCompraCliente", v); }} style={{ marginTop: 3 }} />
                    <Input bsSize="sm" type="text" placeholder="Observaciones" value={p.observaciones}
                      onChange={(e) => { const v = e.target.value; cambiaCampo(p.key, "observaciones", v); }} style={{ marginTop: 3 }} />
                    <Button size="sm" color="link" style={{ fontSize: 11, padding: 0, marginTop: 3 }} onClick={() => llenaResto(p.key)}>
                      todo lo que falta
                    </Button>
                  </th>
                ))}
                <th className="text-right">Sin repartir</th>
              </tr>
            </thead>
            <tbody>
              {lineas.map((m) => {
                const resto = m.cantidad - repartidoModelo(m);
                const esManual = !!manual[m._id];
                const deMas = pctDeMas.includes(m) || m.lineas.some((l) => repartidoLinea(m, l) > l.cantidad);
                return (
                  <React.Fragment key={m._id}>
                    <tr style={deMas ? { background: "#fef2f2" } : undefined}>
                      <td className="text-center">
                        <Button size="sm" color="link" style={{ padding: 0 }}
                          title={abiertos[m._id] ? "Cerrar el modelo" : "Abrir el modelo por color y talla"}
                          onClick={() => setAbiertos((x) => ({ ...x, [m._id]: !x[m._id] }))}>
                          <i className={abiertos[m._id] ? "fas fa-chevron-down" : "fas fa-chevron-right"} />
                        </Button>
                      </td>
                      <td style={{ color: "#94a3b8" }}>{m.renglon}</td>
                      <td>
                        {m.modelo}
                        {esManual ? <div style={{ fontSize: 10, color: "#b45309" }}>corregido a mano</div> : null}
                      </td>
                      <td style={{ fontSize: 12 }}>{m.colores}</td>
                      <td style={{ fontSize: 12 }}>{m.tallas}</td>
                      <td className="text-right">{num(m.cantidad)}</td>
                      {pedidos.map((p) => (
                        <td key={p.key}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Input bsSize="sm" type="number" min="0" max="100" step="any" placeholder="0"
                              value={pctVisible(m, p)}
                              title={esManual ? "Calculado de las piezas; escribir un porcentaje regresa al reparto automatico" : undefined}
                              style={{ textAlign: "right", ...(esManual ? { color: "#b45309" } : {}) }}
                              onChange={(e) => { const v = e.target.value; cambiaPct(p.key, m._id, v); }} />
                            <span style={{ color: "#64748b" }}>%</span>
                          </div>
                          <div style={{ fontSize: 11, color: "#64748b", textAlign: "right" }}>{num(piezasModeloPedido(m, p))} pzas</div>
                        </td>
                      ))}
                      <td className="text-right" style={{ fontWeight: 700, color: colorResto(resto, deMas) }}>
                        {num(resto)} pzas
                      </td>
                    </tr>
                    {abiertos[m._id] ? m.lineas.map((l) => {
                      const restoL = l.cantidad - repartidoLinea(m, l);
                      return (
                        <tr key={l._id} style={{ background: "#fafafa", fontSize: 12 }}>
                          <td></td><td></td>
                          <td style={{ paddingLeft: 18, color: "#64748b" }}>{m.modelo}</td>
                          <td>{l.color}</td>
                          <td>{l.talla}</td>
                          <td className="text-right">{num(l.cantidad)}</td>
                          {pedidos.map((p) => (
                            <td key={p.key}>
                              <Input bsSize="sm" type="number" min="0" style={{ textAlign: "right" }}
                                value={valores[m._id]?.[p.key]?.[l._id] ?? 0}
                                onChange={(e) => { const v = e.target.value; cambiaVariante(m, p.key, l._id, v); }} />
                            </td>
                          ))}
                          <td className="text-right" style={{ color: colorResto(restoL, restoL < 0) }}>{num(restoL)}</td>
                        </tr>
                      );
                    }) : null}
                  </React.Fragment>
                );
              })}
              <tr className="negrita">
                <td colSpan={5} className="text-right">TOTAL</td>
                <td className="text-right">{num(total)}</td>
                {pedidos.map((p) => <td key={p.key} className="text-right">{num(piezasPedido(p))} pzas</td>)}
                <td className="text-right">{num(sinRepartir)} pzas</td>
              </tr>
            </tbody>
          </Table>
        </div>
        <Row>
          <Col>
            <Button size="sm" color="secondary" onClick={() => setPedidos((prev) => [...prev, nuevoPedido(prev.length + 1)])}>
              + Otro pedido
            </Button>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {lineas.length} modelo(s) · {pedidos.length} pedido(s)
        </Label>
        <Button color="success" onClick={dividir} disabled={guardando || !lineas.length}>{guardando ? "Dividiendo..." : "Dividir"}</Button>
        <Button color="secondary" onClick={cerrar}>Cancelar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default DividirPrepedidoModal;
